// Hover tooltips for nodes that a library renders and replaces underneath us,
// the way mobiscroll redraws the schedule board's cards from HTML strings.
// tippy (the vendored v5 global) binds one instance per node and hides it on
// that node's mouseleave, so a node that is replaced while its tooltip is
// showing leaves the popper on screen: a detached node never receives the
// event. Binding per node after every render also meant nothing ever owned
// those instances. One delegate on a stable root creates a tooltip the first
// time a target is hovered, so new nodes need no re-binding, and any target
// that leaves the document takes its tooltip with it.

type TippyInstance = {
  state: { isDestroyed: boolean };
  destroy(): void;
};

type DelegateInstance = {
  destroy(shouldDestroyChildInstances?: boolean): void;
};

type TooltipTarget = Element & { _tippy?: TippyInstance };

export type DelegatedTooltips = { destroy(): void };

const DEFAULT_SELECTOR = "[data-tippy-content]";

const targetsIn = (node: Node, selector: string): TooltipTarget[] => {
  if (!(node instanceof Element)) return [];
  const nested = Array.from(node.querySelectorAll(selector)) as TooltipTarget[];
  return node.matches(selector) ? [node as TooltipTarget, ...nested] : nested;
};

const destroyTooltipOf = (target: TooltipTarget) => {
  const instance = target._tippy;
  if (instance && !instance.state.isDestroyed) instance.destroy();
};

const destroyDetached = (records: MutationRecord[], selector: string) => {
  records
    .flatMap((record) => Array.from(record.removedNodes))
    .flatMap((node) => targetsIn(node, selector))
    .filter((target) => !document.contains(target))
    .forEach(destroyTooltipOf);
};

/**
 * Own every tooltip under `root` until `destroy()` is called. Teardown
 * destroys whatever is still bound under the root and tells the delegate not
 * to touch its children: its own list is never pruned, so it would re-destroy
 * the ones the removal sweep already handled and warn on each.
 */
export function createDelegatedTooltips(
  root: HTMLElement,
  selector = DEFAULT_SELECTOR
): DelegatedTooltips {
  const delegate: DelegateInstance = window.tippy.delegate(root, {
    target: selector,
    onShow: () => window.tippy.hideAll()
  });
  const observer = new MutationObserver((records) =>
    destroyDetached(records, selector)
  );
  observer.observe(root, { childList: true, subtree: true });

  let destroyed = false;
  return {
    destroy() {
      if (destroyed) return;
      destroyed = true;
      destroyDetached(observer.takeRecords(), selector);
      observer.disconnect();
      delegate.destroy(false);
      targetsIn(root, selector).forEach(destroyTooltipOf);
    }
  };
}
